import { tgpu, d } from 'typegpu';
import type { TgpuRoot, TgpuBuffer, TgpuGuardedComputePipeline, TgpuBindGroup, TgpuBindGroupLayout } from 'typegpu';
import type { StorageFlag, UniformFlag } from 'typegpu';
import type { WgslArray } from 'typegpu/data';
import type { SimulationConfig, Resolution, VizMode } from '../config/schema';
import type { System } from '../systems/System';
import { createSystemFromConfig } from '../systems/registry';
import { DataCell } from '../systems/shared/DataCell';
import { isNeighborVizMode } from '../systems/shared/neighbor';

const CopyParams = d.struct({
  canvasRes: d.f32,
  chunkX: d.f32,
  chunkY: d.f32,
  chunkSize: d.f32,
});

export class Simulator {
  private root: TgpuRoot;
  private config: SimulationConfig;
  private system: System;
  private cellCount: number;
  private frameCount = 0;
  private isDivergence = false;
  private isNeighbor = false;
  private isClosenessOfReturn = false;
  private isChunked = false;
  private totalTrials = 1;
  private currentTrial = 0;
  private trialsCompleted = 0;
  private isMultiTrial = false;
  private snapshotTaken = false;
  private searchIter = 0;
  private chunksPerSide = 1;
  private currentChunkX = 0;
  private currentChunkY = 0;
  private completedChunks = 0;
  private totalChunks = 1;
  private compositeBuf: TgpuBuffer<WgslArray<typeof DataCell>> & StorageFlag | null = null;
  private copyPipeline: TgpuGuardedComputePipeline | null = null;
  private copyLayout: TgpuBindGroupLayout | null = null;
  private copyBG: TgpuBindGroup | null = null;
  private copyParamsBuf: TgpuBuffer<typeof CopyParams> & UniformFlag | null = null;

  constructor(root: TgpuRoot, config: SimulationConfig) {
    this.root = root;
    this.config = config;
    this.isClosenessOfReturn = config.vizMode === 'closenessOfReturn';
    this.isDivergence = config.vizMode === 'divergence' || config.vizMode === 'divergenceDistance';
    this.isNeighbor = isNeighborVizMode(config.vizMode);
    this.isChunked = config.chunkSize < config.resolution && !this.isClosenessOfReturn;

    if (this.isChunked) {
      const cs = config.chunkSize;
      this.chunksPerSide = config.resolution / cs;
      this.totalChunks = this.chunksPerSide * this.chunksPerSide;
      this.cellCount = cs * cs;
    } else {
      this.cellCount = config.resolution * config.resolution;
    }

    this.system = createSystemFromConfig(config);
    this.system.build(root, config, this.cellCount);
    this.totalTrials = this.isDivergence ? Math.max(1, Math.floor(config.trials ?? 1)) : 1;
    this.isMultiTrial = this.isDivergence && this.totalTrials > 1;

    if (this.isChunked) {
      this.initChunkedResources();
    }
  }

  get configValue() { return this.config; }

  getData(): any {
    if (this.isChunked) return this.compositeBuf;
    return this.system.getData(this.config.vizMode);
  }

  init(): void {
    this.frameCount = 0;
    this.currentTrial = 0;
    this.trialsCompleted = 0;
    this.snapshotTaken = false;
    this.searchIter = 0;

    if (this.isChunked) {
      this.currentChunkX = 0;
      this.currentChunkY = 0;
      this.completedChunks = 0;
      this.initChunk();
    } else {
      this.system.updateParams(this.config);
      if (this.isDivergence) {
        const seed = this.config.seed + this.currentTrial * 1000;
        this.system.initDivergence(seed, this.config.perturb);
      } else if (this.isNeighbor) {
        this.system.neighborInit(this.config.seed, this.config.perturb);
      } else {
        this.system.initSim();
      }
    }
  }

  private initChunk(): void {
    const ps = this.config.phaseSpace;
    const cps = this.chunksPerSide;
    const cx = this.currentChunkX;
    const cy = this.currentChunkY;
    const xRange = ps.x.max - ps.x.min;
    const yRange = ps.y.max - ps.y.min;
    const chunkConfig: SimulationConfig = {
      ...this.config,
      resolution: this.config.chunkSize as Resolution,
      phaseSpace: {
        ...ps,
        x: { ...ps.x, min: ps.x.min + cx * xRange / cps, max: ps.x.min + (cx + 1) * xRange / cps },
        y: { ...ps.y, min: ps.y.min + cy * yRange / cps, max: ps.y.min + (cy + 1) * yRange / cps },
      },
    };
    this.system.updateParams(chunkConfig);
    this.frameCount = 0;
    this.currentTrial = 0;
    this.trialsCompleted = 0;
    if (this.isDivergence) {
      const seed = this.config.seed + this.currentTrial * 1000;
      this.system.initDivergence(seed, this.config.perturb);
    } else if (this.isNeighbor) {
      this.system.neighborInit(this.config.seed, this.config.perturb);
    } else {
      this.system.initSim();
    }
  }

  step(): void {
    if (this.isChunked) {
      this.stepChunked();
    } else {
      this.stepNormal();
    }
  }

  private stepNormal(): void {
    if (this.isDivergence) {
      this.stepDivergence();
    } else if (this.isNeighbor) {
      this.stepNeighbor();
    } else if (this.isClosenessOfReturn) {
      this.stepClosenessOfReturn();
    } else {
      this.stepDistance();
    }
  }

  private stepChunked(): void {
    if (this.isDivergence) {
      this.stepDivergence();
    } else if (this.isNeighbor) {
      this.stepNeighbor();
    } else {
      this.stepDistance();
    }

    if (this.chunkIsDone()) {
      this.compositeCurrentChunk();
      this.completedChunks++;
      if (this.completedChunks < this.totalChunks) {
        this.advanceChunk();
        this.initChunk();
      }
    }
  }

  private chunkIsDone(): boolean {
    if (this.isMultiTrial) return this.trialsCompleted >= this.totalTrials;
    return this.frameCount >= this.config.maxIter;
  }

  private advanceChunk(): void {
    const idx = this.completedChunks;
    this.currentChunkX = idx % this.chunksPerSide;
    this.currentChunkY = Math.floor(idx / this.chunksPerSide);
  }

  private compositeCurrentChunk(): void {
    if (!this.copyPipeline || !this.copyParamsBuf || !this.compositeBuf || !this.copyLayout) return;
    const cs = this.config.chunkSize;
    this.copyParamsBuf.write({
      canvasRes: this.config.resolution,
      chunkX: this.currentChunkX,
      chunkY: this.currentChunkY,
      chunkSize: cs,
    } as never);

    const srcBuf = this.system.getData(this.config.vizMode) as unknown as TgpuBuffer<WgslArray<typeof DataCell>> & StorageFlag;
    this.copyBG = this.root.createBindGroup(
      this.copyLayout,
      { src: srcBuf, dst: this.compositeBuf, params: this.copyParamsBuf },
    );
    this.copyPipeline.with(this.copyBG).dispatchThreads(cs * cs);
  }

  private initChunkedResources(): void {
    const res = this.config.resolution;
    const cs = this.config.chunkSize;
    const canvasCellCount = res * res;

    const canvasDataArr = d.arrayOf(DataCell, canvasCellCount);
    this.compositeBuf = this.root.createBuffer(canvasDataArr,
      new Array(canvasCellCount).fill({ r: 0, g: 0, b: 0, a: 0 }),
    ).$usage('storage') as unknown as TgpuBuffer<WgslArray<typeof DataCell>> & StorageFlag;

    this.copyParamsBuf = this.root.createBuffer(CopyParams, {
      canvasRes: res, chunkX: 0, chunkY: 0, chunkSize: cs,
    }).$usage('uniform') as unknown as TgpuBuffer<typeof CopyParams> & UniformFlag;

    const chunkDataArr = d.arrayOf(DataCell, cs * cs);
    const copyLayout = tgpu.bindGroupLayout({
      src: { storage: chunkDataArr },
      dst: { storage: canvasDataArr, access: 'mutable' },
      params: { uniform: CopyParams },
    });
    this.copyLayout = copyLayout;
    const cl = copyLayout;
    const tileCopy = (ci: number) => {
      'use gpu';
      const p = cl.$.params;
      const canvasRes = d.u32(p.canvasRes);
      const chunkSz = d.u32(p.chunkSize);
      const sx = ci % chunkSz;
      const sy = ci / chunkSz;
      const dx = d.u32(p.chunkX * chunkSz) + sx;
      const dy = d.u32(p.chunkY * chunkSz) + sy;
      if (dx < canvasRes && dy < canvasRes) {
        cl.$.dst[dy * canvasRes + dx] = DataCell(cl.$.src[ci]);
      }
    };
    this.copyPipeline = this.root.createGuardedComputePipeline(tileCopy);
  }

  private stepDistance(): void {
    const batchSize = Math.min(this.config.iterationsPerFrame, this.config.maxIter - this.frameCount);
    for (let i = 0; i < batchSize; i++) {
      this.system.stepSim();
      this.frameCount++;
    }
    this.system.accumulateSim();
  }

  private stepNeighbor(): void {
    const batchSize = Math.min(this.config.iterationsPerFrame, this.config.maxIter - this.frameCount);
    for (let i = 0; i < batchSize; i++) {
      this.system.neighborStep();
      this.frameCount++;
    }
  }

  private stepDivergence(): void {
    const batchSize = Math.min(20, this.config.maxIter - this.frameCount);
    for (let i = 0; i < batchSize; i++) {
      this.system.divergenceStep();
      this.frameCount++;
    }
    if (this.isMultiTrial && this.frameCount >= this.config.maxIter) {
      this.system.blendDivergenceTrial(this.trialsCompleted);
      this.trialsCompleted++;
      if (this.trialsCompleted < this.totalTrials) {
        this.currentTrial = this.trialsCompleted;
        const seed = this.config.seed + this.currentTrial * 1000;
        this.system.initDivergence(seed, this.config.perturb);
        this.frameCount = 0;
      }
    }
  }

  private stepClosenessOfReturn(): void {
    const warmupEnd = this.config.iterationsBeforeSnapshot;
    const searchStart = warmupEnd + this.config.iterationsAfterSnapshot;
    const isWarmup = this.frameCount < searchStart;
    const batchSize = isWarmup ? Math.max(1, this.config.iterationsPerFrame) : Math.max(1, this.config.iterationsPerFrame);
    for (let i = 0; i < batchSize && this.frameCount < this.config.maxIter; i++) {
      if (!this.snapshotTaken && this.frameCount >= warmupEnd) {
        this.system.takeSnapshot();
        this.snapshotTaken = true;
        this.searchIter = 0;
      }
      if (this.snapshotTaken && this.frameCount >= searchStart) {
        this.system.closenessOfReturnStep(this.searchIter, this.config.minimumImprovementToUpdate);
        this.searchIter++;
      } else {
        this.system.stepSim();
      }
      this.frameCount++;
    }
  }

  getFrameCount(): number {
    if (this.isChunked) {
      return this.completedChunks * this.config.maxIter + this.frameCount;
    }
    return this.trialsCompleted * this.config.maxIter + this.frameCount;
  }

  isComplete(): boolean {
    if (this.isChunked) return this.completedChunks >= this.totalChunks;
    if (this.isMultiTrial) return this.trialsCompleted >= this.totalTrials;
    return this.frameCount >= this.config.maxIter;
  }

  canRenderMore(): boolean {
    return !this.isChunked && !this.isMultiTrial;
  }
}
